import React from 'react';
import { X, History } from 'lucide-react';
import { EditHistoryChange, EditHistoryEntry, MongoDate } from '../../../api/types';

interface EditHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    history?: EditHistoryEntry[];
    vendorName?: string;
}

const formatDate = (date: string | MongoDate | undefined): string => {
    if (!date) return '---';
    const raw = typeof date === 'string' ? date : date.$date;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '---' : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    });
};

interface ParsedChange {
    field: string;
    oldValue: string;
    newValue: string;
}

const formatChangeValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return 'none';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

const normalizeStructuredChanges = (changes?: EditHistoryChange[]): ParsedChange[] => {
    return (changes || []).map((change) => ({
        field: change.field,
        oldValue: formatChangeValue(change.from),
        newValue: formatChangeValue(change.to),
    }));
};

const parseChangeDetails = (details: string): { message: string, changes: ParsedChange[] } => {
    const changedPrefix = "Changed: ";
    const changedIndex = details.indexOf(changedPrefix);

    if (changedIndex === -1) {
        return { message: details, changes: [] };
    }

    const message = details.substring(0, changedIndex).trim();
    const changesString = details.substring(changedIndex + changedPrefix.length);

    // Split by comma followed by field name pattern: ", field_name: "
    // We use a regex to look for ", " followed by a word and a colon
    const changes: ParsedChange[] = [];
    const parts = changesString.split(/,\s*(?=\w+:)/);

    parts.forEach(part => {
        const colonIndex = part.indexOf(':');
        if (colonIndex !== -1) {
            const field = part.substring(0, colonIndex).trim();
            const values = part.substring(colonIndex + 1).split(' -> ');
            if (values.length === 2) {
                changes.push({
                    field,
                    oldValue: values[0].trim(),
                    newValue: values[1].trim()
                });
            }
        }
    });

    return { message, changes };
};

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({ isOpen, onClose, history, vendorName }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-opacity">
            <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">Transaction History</h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                            Audit for <span className="text-slate-700 font-semibold">{vendorName || 'this transaction'}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X size={22} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-0">
                    {!history || history.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center px-10">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                <History size={32} className="text-slate-300" />
                            </div>
                            <p className="text-slate-600 font-bold">No history records found</p>
                            <p className="text-sm text-slate-400 mt-1">Changes made via the dashboard will appear here automatically.</p>
                        </div>
                    ) : (
                        <div className="">
                            {[...history].reverse().map((entry, index) => {
                                const parsed = parseChangeDetails(entry.details);
                                const changes = normalizeStructuredChanges(entry.changes);
                                const fallbackChanges = changes.length > 0 ? changes : parsed.changes;
                                const message = parsed.message;
                                return (
                                    <div key={index} className="px-8 py-6 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <div className="flex flex-col gap-0.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                                                    <span className="text-sm font-bold text-slate-900">
                                                        {entry.action.replace(/_/g, ' ').toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-slate-500 ml-3.5">
                                                    <span className="font-medium px-1.5 py-0.5 rounded text-[10px] text-slate-600 bg-slate-100">
                                                        {entry.updated_by_display || (entry.updated_by.length > 12 ? `${entry.updated_by.substring(0, 8)}...` : entry.updated_by)}
                                                    </span>
                                                    <span>•</span>
                                                    <span>{formatDate(entry.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ml-3.5">
                                            {fallbackChanges.length > 0 ? (
                                                <div className="space-y-3">
                                                    {message && message !== entry.details && (
                                                        <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                            {message}
                                                        </p>
                                                    )}

                                                    <div className="space-y-2">
                                                        {fallbackChanges.map((change, cIdx) => (
                                                            <div key={cIdx} className="flex flex-col gap-1">
                                                                <span className="text-[11px] font-bold text-slate-400 uppercase">
                                                                    {change.field.replace(/_/g, ' ')}
                                                                </span>
                                                                <div className="flex items-center gap-2 text-sm">
                                                                    <span className="text-slate-400 line-through decoration-slate-300">
                                                                        {change.oldValue || 'none'}
                                                                    </span>
                                                                    <span className="text-indigo-400">→</span>
                                                                    <span className="text-slate-900 font-bold px-2 py-0.5 bg-indigo-50/50 border border-indigo-100 rounded-md">
                                                                        {change.newValue}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                    {entry.details}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-100 transition-all shadow-sm active:scale-95"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditHistoryModal;
